import React, { useState, useEffect } from 'react';
import { 
  Users as UsersIcon, 
  Plus, 
  Edit, 
  Trash2, 
  Eye, 
  Search,
  Filter,
  MoreVertical,
  Key,
  Activity,
} from 'lucide-react';
import { Container, Row, Col, Button, Form, Table, Pagination, Card, Badge, InputGroup, Dropdown, Alert, Spinner } from 'react-bootstrap';
import { useLocation } from 'react-router-dom';
import UserRegistrationModal from './UserRegistrationModal';
import UserDetailsModal from './UserDetailsModal';
import EditUserModal from './EditUserModal';
import PasswordResetModal from './PasswordResetModal';
import UserLogsModal from './UserLogsModal';
import { apiService } from '../services/api';
import { useUser } from '../contexts/UserContext';
import './Dashboard.css';

const Users = () => {
  const location = useLocation();
  const [showUserModal, setShowUserModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPasswordResetModal, setShowPasswordResetModal] = useState(false);
  const [showUserLogsModal, setShowUserLogsModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterLockoutStatus, setFilterLockoutStatus] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  
  const { user: currentUser, canEditUser } = useUser();

  useEffect(() => {
    // Force Poppins font on all elements in this component
    const style = document.createElement('style');
    style.textContent = `
      .users-wrapper *,
      .users-wrapper *::before,
      .users-wrapper *::after {
        font-family: 'Poppins', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, []);

  // Load users from API
  useEffect(() => {
    if (currentUser) {
      loadUsers();
    }
  }, [currentUser]);

  // Check if modal should be opened from navigation state
  useEffect(() => {
    if (location.state?.openAddUserModal) {
      setShowUserModal(true);
      // Clear the state to prevent reopening on re-render
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await apiService.getUsers();
      if (response.status && response.data) {
        let filteredUsers = response.data;
        
        // If current user is companyAdmin, only show users from their company
        if (currentUser?.role === 'companyAdmin') {
          filteredUsers = response.data.filter(user => user.assetId === currentUser.assetId);
        }
        
        setUsers(filteredUsers);
        return filteredUsers;
      }
    } catch (err) {
      setError(err.message || 'Failed to load users');
      return [];
    } finally {
      setLoading(false);
    }
  };

  const getCompanyName = (user) => {
    if (!user.assetId || !user.company) return 'No company';
    return `${user.company.company} ${user.company.branch ? `- ${user.company.branch}` : ''}`;
  };

  const handleShowUserModal = () => {
    setShowUserModal(true);
  };

  const handleHideUserModal = () => {
    setShowUserModal(false);
  };

  const handleShowDetailsModal = (user) => {
    setSelectedUser(user);
    setShowDetailsModal(true);
  };

  const handleHideDetailsModal = () => {
    setShowDetailsModal(false);
    setSelectedUser(null);
  };

  const handleShowEditModal = (user) => {
    setSelectedUser(user);
    setShowEditModal(true);
  };

  const handleHideEditModal = () => {
    setShowEditModal(false);
    setSelectedUser(null);
  };

  const handleShowPasswordResetModal = (user) => {
    setSelectedUser(user);
    setShowPasswordResetModal(true);
  };

  const handleHidePasswordResetModal = () => {
    setShowPasswordResetModal(false);
    setSelectedUser(null);
  };

  const handleShowUserLogsModal = (user) => {
    setSelectedUser(user);
    setShowUserLogsModal(true);
  };

  const handleHideUserLogsModal = () => {
    setShowUserLogsModal(false);
    setSelectedUser(null);
  };


  const handleUserAdded = async (newUser) => {
    try {
      setSuccess('User added successfully!');
      const updatedUsers = await loadUsers(); // Reload users list
      const refreshedUser = updatedUsers?.find(user => `${user.id}` === `${newUser?.id}`) || newUser;
      setSelectedUser(refreshedUser);
      setShowDetailsModal(true);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to add user');
    }
  };

  const handleUserUpdated = async () => {
    try {
      setSuccess('User updated successfully!');
      await loadUsers(); // Reload users list
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to update user');
    }
  };

  const handlePasswordReset = async () => {
    try {
      setSuccess('Password reset successfully!');
      await loadUsers(); // Reload users list
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to reset password');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await apiService.deleteUser(userId);
        setSuccess('User deleted successfully!');
        await loadUsers(); // Reload users list
        setTimeout(() => setSuccess(''), 3000);
      } catch (err) {
        setError(err.message || 'Failed to delete user');
      }
    }
  };


  const handleLockoutChange = async (userId, isLockedOut) => {
    try {
      await apiService.updateUserLockoutStatus(userId, isLockedOut);
      setSuccess(`User ${isLockedOut ? 'locked out' : 'unlocked'} successfully!`);
      await loadUsers(); // Reload users list
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to update user lockout status');
    }
  };

  // Filter and search users
  const filteredUsers = users.filter(user => {
    const matchesSearch = 
      user.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.role?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesLockoutStatus = filterLockoutStatus === 'all' || 
      (filterLockoutStatus === 'active' && !user.isLockedOut) ||
      (filterLockoutStatus === 'locked' && user.isLockedOut);
    
    return matchesSearch && matchesLockoutStatus;
  });

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);

  if (loading) {
    return (
      <div className="users-wrapper">
        <div className="modern-dashboard">
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
            <div className="text-center">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-3">Loading users...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="users-wrapper">
      <div className="modern-dashboard">
        {/* Header */}
        <div className="dashboard-header-section mb-4">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h1 className="dashboard-title">Users Management</h1>
              <p className="dashboard-subtitle">Manage your team members and their permissions</p>
            </div>
            <div className="dashboard-actions">
              {(currentUser?.role === 'superAdmin' || currentUser?.role === 'companyAdmin') && (
                <Button variant="primary" onClick={handleShowUserModal}>
                  <Plus size={16} className="me-2" />
                  Add New User
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Alerts */}
        {error && (
          <Alert variant="danger" onClose={() => setError('')} dismissible>
            {error}
          </Alert>
        )}
        {success && (
          <Alert variant="success" onClose={() => setSuccess('')} dismissible>
            {success}
          </Alert>
        )}

        {/* Filters and Search */}
        <Card className="mb-4">
          <Card.Body>
            <Row>
              <Col md={9}>
                <InputGroup>
                  <InputGroup.Text>
                    <Search size={16} />
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder="Search users..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </InputGroup>
              </Col>
              <Col md={3}>
                <Form.Select
                  value={filterLockoutStatus}
                  onChange={(e) => setFilterLockoutStatus(e.target.value)}
                >
                  <option value="all">All Users</option>
                  <option value="active">Active</option>
                  <option value="locked">Locked Out</option>
                </Form.Select>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Users Table */}
        <Card>
          <Card.Header className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Users List</h5>
            <span className="text-muted">{filteredUsers.length} users found</span>
          </Card.Header>
          <Card.Body>
            <Table responsive className="modern-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Department</th>
                  <th>Company</th>
                  <th>Lockout Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentUsers.map((user) => (
                  <tr key={user.id}>
                    <td>
                      <div>
                        <div>{user.firstName} {user.lastName}</div>
                        <small className="text-muted">ID: {user.id}</small>
                      </div>
                    </td>
                    <td>{user.email}</td>
                    <td>
                      <Badge bg="light" text="dark">{user.role || 'user'}</Badge>
                    </td>
                    <td>{user.department || 'N/A'}</td>
                    <td>
                      <span className="text-muted" style={{ fontSize: '0.9em' }}>
                        {getCompanyName(user)}
                      </span>
                    </td>
                    <td>
                      <Badge bg={user.isLockedOut === true ? 'danger' : 'success'}>
                        {user.isLockedOut === true ? 'Locked Out' : 'Active'}
                      </Badge>
                    </td>
                    <td>
                      <Dropdown>
                        <Dropdown.Toggle variant="light" size="sm">
                          <MoreVertical size={16} />
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                          <Dropdown.Item 
                            onClick={() => handleShowDetailsModal(user)}
                          >
                            <Eye size={14} className="me-2" />
                            View Details
                          </Dropdown.Item>
                          <Dropdown.Item 
                            onClick={() => handleShowUserLogsModal(user)}
                          >
                            <Activity size={14} className="me-2" />
                            View Logs
                          </Dropdown.Item>
                          {canEditUser(user) && (
                            <>
                              <Dropdown.Item 
                                onClick={() => handleShowEditModal(user)}
                              >
                                <Edit size={14} className="me-2" />
                                Edit User
                              </Dropdown.Item>
                              <Dropdown.Item 
                                onClick={() => handleLockoutChange(user.id, user.isLockedOut === true ? false : true)}
                                className={user.isLockedOut ? 'text-success' : 'text-warning'}
                              >
                                {user.isLockedOut === true ? 'Unlock User' : 'Lock Out User'}
                              </Dropdown.Item>
                              <Dropdown.Divider />
                              <Dropdown.Item 
                                className="text-danger"
                                onClick={() => handleDeleteUser(user.id)}
                              >
                                <Trash2 size={14} className="me-2" />
                                Delete User
                              </Dropdown.Item>
                            </>
                          )}
                          {currentUser?.role === 'superAdmin' && (
                            <>
                              <Dropdown.Divider />
                              <Dropdown.Item 
                                onClick={() => handleShowPasswordResetModal(user)}
                              >
                                <Key size={14} className="me-2" />
                                Reset Password
                              </Dropdown.Item>
                            </>
                          )}
                        </Dropdown.Menu>
                      </Dropdown>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>

            {/* Pagination */}
            {(totalPages > 1 || true) && (
              <div className="d-flex justify-content-between align-items-center mt-4 flex-wrap gap-3">
                {/* Items per page selector */}
                <div className="d-flex align-items-center gap-2">
                  <span className="text-muted" style={{ fontSize: '0.875rem', whiteSpace: 'nowrap' }}>
                    Show:
                  </span>
                  <Form.Select
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(parseInt(e.target.value, 10));
                      setCurrentPage(1);
                    }}
                    size="sm"
                    style={{ width: 'auto', minWidth: '80px' }}
                  >
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </Form.Select>
                  <span className="text-muted" style={{ fontSize: '0.875rem', whiteSpace: 'nowrap' }}>
                    per page
                  </span>
                </div>
                
                {/* Pagination controls */}
                {totalPages > 1 && (
                  <Pagination className="mb-0">
                  <Pagination.First 
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                  />
                  <Pagination.Prev 
                    onClick={() => setCurrentPage(currentPage - 1)}
                    disabled={currentPage === 1}
                  />
                  
                  {/* Show page numbers with ellipsis */}
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => {
                    // Show first 3 pages, last 3 pages, and current page with context
                    const shouldShow = 
                      page <= 3 || 
                      page >= totalPages - 2 || 
                      Math.abs(page - currentPage) <= 1;
                    
                    if (!shouldShow) {
                      // Show ellipsis for gaps
                      if (page === 4 && currentPage > 5) {
                        return <Pagination.Ellipsis key={`ellipsis-${page}`} />;
                      }
                      if (page === totalPages - 3 && currentPage < totalPages - 4) {
                        return <Pagination.Ellipsis key={`ellipsis-${page}`} />;
                      }
                      return null;
                    }
                    
                    return (
                      <Pagination.Item
                        key={page}
                        active={page === currentPage}
                        onClick={() => setCurrentPage(page)}
                      >
                        {page}
                      </Pagination.Item>
                    );
                  })}
                  
                  <Pagination.Next 
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  />
                  <Pagination.Last 
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                  />
                </Pagination>
                )}
              </div>
            )}
          </Card.Body>
        </Card>
      </div>

      {/* User Registration Modal */}
      <UserRegistrationModal 
        show={showUserModal} 
        onHide={handleHideUserModal} 
        onUserAdded={handleUserAdded} 
        currentUser={currentUser}
      />

      {/* User Details Modal */}
      <UserDetailsModal
        show={showDetailsModal}
        user={selectedUser}
        onHide={handleHideDetailsModal}
      />

      {/* Edit User Modal */}
      <EditUserModal
        show={showEditModal}
        user={selectedUser}
        onHide={handleHideEditModal}
        onUserUpdated={handleUserUpdated}
      />

      {/* Password Reset Modal */}
      <PasswordResetModal
        show={showPasswordResetModal}
        user={selectedUser}
        onHide={handleHidePasswordResetModal}
        onPasswordReset={handlePasswordReset}
      />

      {/* User Logs Modal */}
      <UserLogsModal
        show={showUserLogsModal}
        user={selectedUser}
        onHide={handleHideUserLogsModal}
      />

    </div>
  );
};

export default Users; 